import { useEffect, useState } from "react";
import { Container, Group, SimpleGrid, Stack, Pagination } from "@mantine/core";
import { router } from "@inertiajs/react";
import { L2KH1, L2KText } from "../../components/ui/Typography";
import AuthenticatedLayout from "../../components/layouts/AuthenticatedLayout";
import { PostCard } from "../../components/posts/PostCard";
import { PostListItem } from "../../components/posts/PostListItem";
import { ViewModeToggle, ViewMode } from "../../components/posts/ViewModeToggle";
import type { PostSummary } from "../../types/post";
import { SEO } from "../../components/common/SEO";

interface Props {
  posts: PostSummary[];
  pagination: { page: number; total_pages: number; total_count: number };
}

const STORAGE_KEY = "blog_view_mode";

export default function PostsIndex({ posts, pagination }: Props) {
  const [mode, setMode] = useState<ViewMode>("grid");

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY) as ViewMode | null;
    if (saved === "grid" || saved === "list") setMode(saved);
  }, []);

  const updateMode = (m: ViewMode) => {
    setMode(m);
    localStorage.setItem(STORAGE_KEY, m);
  };

  return (
    <AuthenticatedLayout>
      <SEO
        title="Posts — Urbankiz Stories & Tutorials | Learntokiz"
        description="Long-form writing, podcast episodes, and tutorials on urbankiz — technique, culture, and community."
        image="/images/og-image-blog.png"
        url="https://learntokiz.io/posts"
      />
      <Container size="lg" py="xl">
        <Group justify="space-between" align="flex-end" mb="lg">
          <Stack gap={4}>
            <L2KH1>Posts</L2KH1>
            <L2KText variant="secondary">
              Long-form writing and podcast episodes.
            </L2KText>
          </Stack>
          <ViewModeToggle value={mode} onChange={updateMode} />
        </Group>

        {posts.length === 0 ? (
          <L2KText variant="secondary">No posts yet.</L2KText>
        ) : mode === "grid" ? (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
            {posts.map((p) => (
              <PostCard key={p.id} post={p} />
            ))}
          </SimpleGrid>
        ) : (
          <Stack gap="md">
            {posts.map((p) => (
              <PostListItem key={p.id} post={p} />
            ))}
          </Stack>
        )}

        {pagination.total_pages > 1 && (
          <Group justify="center" mt="xl">
            <Pagination
              value={pagination.page}
              total={pagination.total_pages}
              onChange={(page) => router.get("/posts", { page })}
            />
          </Group>
        )}
      </Container>
    </AuthenticatedLayout>
  );
}
